import { defineStore } from 'pinia'
import { ref } from 'vue'
import { useModalStore } from '@/stores/modal'
import { useImagesStore } from '@/stores/images'
import { useAIStore } from '@/stores/openai'
import { useDraftsStore } from '@/stores/drafts'
import { useEditorStore } from '@/stores/editor'
import { http } from '@/helpers/http'
import { DOMParser as ProseMirrorDOMParser } from '@tiptap/pm/model'
import { NodeSelection } from '@tiptap/pm/state'
import type { TSearchImage, TUserImage, TImageQueueItem } from '@/types/images'
import type { Node } from '@tiptap/pm/model'
import type { Editor } from '@tiptap/core'
import type { AIImage } from '@/extensions/tiptap/AIImagesWrapper'
import { imageFramingPair } from '@/helpers/image-prompts'

// Hand-kept mirror of the private ImageGenerationController::MAX_RETRIES in the backend; past
// this the cron's findAndResetRetryableErrorImage no longer revives an error row.
const MAX_IMAGE_RETRIES = 5

export const useEditorImagesStore = defineStore('editor-images', () => {
  const modal = useModalStore()
  const image = useImagesStore()
  const drafts = useDraftsStore()
  const ai = useAIStore()
  const isPickingImage = ref<boolean>(false)
  const checkingTimer = ref<ReturnType<typeof setTimeout> | null>(null)

  async function pickImage(tab?: string): Promise<string | null> {
    isPickingImage.value = true
    const response: TUserImage | TSearchImage | undefined = await modal.open(
      () => import('@/components/modal/DesModalImagePicker.vue'),
      {
        tab: tab ?? 'your',
      },
    )

    if (!response) {
      isPickingImage.value = false
      return null
    }

    if (!('id' in response)) {
      const { url } = await image.copyStockImage(response)
      isPickingImage.value = false
      return url
    }

    isPickingImage.value = false
    return response.url
  }

  async function pickImagePreloaded(tab: string = ''): Promise<{
    url: string
    thumb_url?: string
    title?: string
    height: number
    width: number
  } | null> {
    isPickingImage.value = true
    const response: TUserImage | TSearchImage | undefined = await modal.open(
      () => import('@/components/modal/DesModalImagePicker.vue'),
      { tab: tab ?? 'your' },
    )

    isPickingImage.value = false
    if (!response) {
      return null
    }
    if (!('id' in response)) {
      const { thumb_url, url, title, width, height } = response
      return { thumb_url, url, title, width, height }
    }
    const { url, display_name, width, height } = response
    return { url, title: display_name, width, height }
  }

  function insertPreloadImage(
    editor: Editor,
    src: {
      url: string
      thumb_url?: string
      title?: string
      height: number
      width: number
    },
  ) {
    const { selection } = editor.state
    const toReplace = selection instanceof NodeSelection && selection.node.type.name === 'customImage'

    console.log('[insertPreloadImage]', {
      selectionType: selection.constructor.name,
      from: selection.from,
      to: selection.to,
      isActiveCustomImage: editor.isActive('customImage'),
      toReplace,
      nodeAttrs: toReplace ? selection.node.attrs : null,
    })

    // info for later preload
    let alt: string | undefined

    if (src.thumb_url) {
      alt = `@userUpload@unsplash@${src.url}&id=${src.title}`
    } else if (src.url?.startsWith('https://oaidalleapiprod')) {
      alt = `@userUpload@wordgenie@${src.url}`
    }

    editor
      .chain()
      .focus()
      .setTextSelection({ from: toReplace ? selection.from : selection.to, to: selection.to })
      .setImage({
        ...(toReplace && { width: selection.node.attrs.width, float: selection.node.attrs.float }),
        src: src.thumb_url ?? src.url,
        ...(alt && { alt }),
        ...(src.width && src.height && { size: { width: src.width, height: src.height } }),
      })
      .run()

    if (alt) {
      findImagesToUpload(editor)
    }
  }

  async function findImagesToUpload(editor: Editor): Promise<void> {
    const { doc } = editor.state
    const imagesToReplace: { pos: number; node: any }[] = []

    doc.descendants((node, pos) => {
      if (node.type.name === 'customImage' && node.attrs.alt?.startsWith('@userUpload@')) {
        const url = node.attrs.alt.split('@')[3]
        if (url) {
          imagesToReplace.push({ pos, node })
        }
      }
    })

    if (imagesToReplace.length === 0) return

    let tr = editor.state.tr

    for (const { pos, node } of imagesToReplace) {
      const url = node.attrs.alt.split('@')[3]
      const originalAlt = node.attrs.alt

      tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, alt: '' })

      try {
        const result = await image.saveImage(url)
        const newAttrs = {
          ...node.attrs,
          src: result.url,
          alt: originalAlt.split('@')[2],
        }
        tr = tr.setNodeMarkup(pos, undefined, newAttrs)
      } catch (error) {
        console.error('Failed to save image:', error)
        tr = tr.setNodeMarkup(pos, undefined, { ...node.attrs, alt: originalAlt })
      }
    }

    editor.view.dispatch(tr)
  }

  async function findImagesForPlaceholders(
    type: 'wordgenie' | 'stack' = 'wordgenie',
    draftKeywords: string[] | null = null,
    resetSkipImages: boolean = true,
  ): Promise<void> {
    const editorStore = useEditorStore()
    const editor = editorStore.getEditor()
    if (!editor) return
    const { doc } = editor.state
    const imagesToReplace: { pos: number; node: any }[] = []

    doc.descendants((node, pos) => {
      if (node.type.name === 'customImage' && node.attrs.alt?.startsWith('@wordgenieImage@placeholder@')) {
        const url = node.attrs.alt.split('@')[3]
        if ((type === 'stack' && url === 'null') || (type === 'wordgenie' && url !== 'null'))
          if (url) {
            imagesToReplace.push({ pos, node })
          }
      }
    })

    if (resetSkipImages === true) {
      image.skipImages = []
    }

    const findCurrentPosition = (nodeAlt: string): number => {
      let pos = -1
      editor.state.doc.descendants((currentNode, currentNodePos) => {
        if (currentNode.type.name === 'customImage' && currentNode.attrs.alt === nodeAlt && pos === -1) {
          pos = currentNodePos
          return false
        }
      })
      return pos
    }

    for (const { node } of imagesToReplace) {
      try {
        let result: TUserImage[]

        let keywords = draftKeywords ? draftKeywords.join(', ') : node.attrs.alt.split('@')[3]
        if (!keywords || keywords === 'null') {
          keywords = node.attrs.alt.split('@')[4]
        }
        console.log('Finding image for keywords:', keywords, 'from', draftKeywords ? 'draft' : 'node')

        if (type === 'stack') {
          const found = await image.getImagesByKeywords(keywords, 'landscape')
          if (!found) throw new Error('No image found for keywords')
          result = [found]
        } else {
          result = await ai.generateMultipleImageV2(keywords, 'landscape', 1)
        }

        const pos = findCurrentPosition(node.attrs.alt)
        if (pos === -1) {
          console.log('Node no longer exists in document')
          continue
        }

        if (result && result[0]) {
          const tr = editor.state.tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            src: result[0].filelink,
            alt: keywords,
          })
          if (result[0].original_url) {
            image.skipImages.push(result[0].original_url ?? '')
          }
          editor.view.dispatch(tr)
        } else {
          console.log('Failed to generate image:', keywords)
          const tr = editor.state.tr.deleteRange(pos, pos + node.nodeSize)
          editor.view.dispatch(tr)
        }
      } catch (error) {
        console.log('Failed to save image:', error)

        const pos = findCurrentPosition(node.attrs.alt)
        if (pos === -1) {
          console.log('Node no longer exists in document')
          continue
        }

        const tr = editor.state.tr.deleteRange(pos, pos + node.nodeSize)
        editor.view.dispatch(tr)
      }
    }
  }

  function addEnhancedImages(): boolean {
    const editorStore = useEditorStore()
    const editor = editorStore.getEditor()
    if (!editor) return false

    const { doc, tr } = editor.state
    const paragraphs: Array<{ node: Node; pos: number }> = []
    doc.descendants((node, pos) => {
      if (node.type.name === 'paragraph') {
        paragraphs.push({ node, pos })
      }
    })

    const rangeBetweenIndexes = Math.round(paragraphs.length / 6) || 1
    const indexes: number[] = []
    for (let i = rangeBetweenIndexes; i < paragraphs.length; i += rangeBetweenIndexes) {
      indexes.push(i)
    }

    for (let idx = indexes.length - 1; idx >= 0; idx--) {
      const i = indexes[idx]!
      const { node, pos } = paragraphs[i]!
      const insertPos = pos + node.nodeSize

      const imageNode = editor.schema.nodes.customImage?.create({
        src: 'https://stage.designrr.io/dashboard/images/placeholder.png',
        alt: `@wordgenieImage@placeholder@null@${node.attrs.wordgenieKeywords}`,
        width: '100%',
      })

      if (!imageNode) continue
      const newParagraph = editor.schema.nodes.paragraph?.create({ dir: node.attrs.dir }, [imageNode])

      if (!newParagraph) continue
      tr.insert(insertPos, newParagraph)
    }
    editor.view.dispatch(tr)

    return true
  }

  function checkParagraphsForKeywords(): boolean | null {
    const editorStore = useEditorStore()
    const editor = editorStore.getEditor()
    if (!editor) return null

    let found = false
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'paragraph' && node.attrs.wordgenieKeywords) {
        found = true
        return false
      }
    })
    return found
  }

  async function checkPlaceholders() {
    const editorStore = useEditorStore()
    const editor = editorStore.getEditor()
    if (!editor) return

    const findCurrentPosition = (id: number): { pos: number; node: Node } | null => {
      let result: { pos: number; node: Node } | null = null
      editor.state.doc.descendants((node, pos) => {
        if (
          node.type.name === 'aiImagesWrapper' &&
          node.attrs.images?.some((e: AIImage) => e.id === id) &&
          result === null
        ) {
          result = { pos, node }
          return false
        }
      })
      return result
    }

    const imagesIds: number[] = []
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'aiImagesWrapper') {
        node.attrs.images.map((image: AIImage) => {
          if (!image.src) {
            imagesIds.push(image.id)
          }
        })
      }
    })

    if (imagesIds.length === 0) return

    try {
      const res = await http.post<TImageQueueItem[]>(`/ai/get_images_queue`, { ids: imagesIds })

      // Successful images: done with a URL
      const successfulImages = res.filter((item) => item.status === 'done' && item.url)

      // Terminal failures the cron will never resolve: done with no URL, an unsplash error
      // (unsplash errors are never retried), or any source that exhausted its retries.
      const isTerminalFailure = (item: TImageQueueItem): boolean =>
        (item.status === 'done' && !item.url) ||
        (item.status === 'error' && (item.source === 'unsplash' || item.retries >= MAX_IMAGE_RETRIES))

      const failedImages = res.filter(isTerminalFailure)

      // Update successful images with their URLs
      for (const item of successfulImages) {
        const found = findCurrentPosition(item.id)
        if (!found) continue

        const { pos, node } = found
        const images = node.attrs.images.map((img: AIImage) => (img.id === item.id ? { ...img, src: item.url } : img))

        const tr = editor.state.tr.setNodeMarkup(pos, undefined, {
          ...node.attrs,
          images,
        })
        editor.view.dispatch(tr)
      }

      // Remove failed images from their wrappers
      for (const item of failedImages) {
        const found = findCurrentPosition(item.id)
        if (!found) continue

        const { pos, node } = found
        const remainingImages = node.attrs.images.filter((img: AIImage) => img.id !== item.id)

        if (remainingImages.length === 0) {
          // No images left, delete the entire wrapper
          const tr = editor.state.tr.deleteRange(pos, pos + node.nodeSize)
          editor.view.dispatch(tr)
        } else {
          // Update the wrapper with remaining images
          const tr = editor.state.tr.setNodeMarkup(pos, undefined, {
            ...node.attrs,
            images: remainingImages,
          })
          editor.view.dispatch(tr)
        }
      }

      // Still worth polling: queued or processing, or an error the cron may still retry.
      const stillPendingImages = res.filter(
        (item) =>
          item.status === 'in_progress' ||
          item.status === 'idle' ||
          (item.status === 'error' && !isTerminalFailure(item)),
      )

      if (checkingTimer.value) clearTimeout(checkingTimer.value)

      if (stillPendingImages.length === 0) {
        return
      }

      checkingTimer.value = setTimeout(() => {
        checkPlaceholders()
      }, 5000)
    } catch (error) {
      console.error('Error checking placeholders:', error)
    }
  }

  async function getImagesForPlaceholders(keywords: string[] | null) {
    const editorStore = useEditorStore()
    const editor = editorStore.getEditor()
    if (!editor) return

    const { schema } = editor.state
    const parser = ProseMirrorDOMParser.fromSchema(schema)

    const placeholders: { originalAlt: string; keywords: string[] }[] = []

    const docKeywords = keywords || []
    editor.state.doc.descendants((node) => {
      if (node.type.name === 'customImage' && node.attrs.alt?.startsWith('@wordgenieImage@placeholder@')) {
        const altKeywords = node.attrs.alt.split('@')[4]?.split(', ') ?? []
        const keywords = altKeywords[0] != 'null' ? altKeywords : docKeywords
        placeholders.push({ originalAlt: node.attrs.alt, keywords })
      }
    })

    const skippedAlts: string[] = []
    const validPlaceholders: { originalAlt: string; keywords: string[] }[] = []
    for (const { originalAlt, keywords } of placeholders) {
      const cleanedKeywords = keywords.map((k) => k.trim()).filter((k) => k !== '')
      if (cleanedKeywords.length === 0) {
        skippedAlts.push(originalAlt)
        continue
      }
      validPlaceholders.push({ originalAlt, keywords: cleanedKeywords })
    }

    if (skippedAlts.length > 0) {
      window?.Bugsnag?.notify(
        new Error('AI image placeholder skipped: no usable keywords'),
        (event: { addMetadata: (key: string, data: object) => void }) => {
          event.addMetadata('aiImagePlaceholders', {
            draftId: drafts.draft?.id ?? null,
            skippedCount: skippedAlts.length,
            totalCount: placeholders.length,
            sampleAlts: skippedAlts.slice(0, 5),
          })
        },
      )
    }

    for (const { originalAlt, keywords } of validPlaceholders) {
      try {
        const descriptions = imageFramingPair(keywords)
        const res = await http.post<{ content: string }>(`/ai/get_images_wrapper`, {
          keywords,
          descriptions,
          description: descriptions[0] ?? keywords.join(','),
          draft_id: drafts.draft?.id ?? null,
        })

        let pos = -1
        editor.state.doc.descendants((node, currentPos) => {
          if (node.type.name === 'customImage' && node.attrs.alt === originalAlt && pos === -1) {
            pos = currentPos
            return false
          }
        })

        if (pos !== -1) {
          const tempDiv = document.createElement('div')
          tempDiv.innerHTML = res.content
          const parsed = parser.parse(tempDiv)

          let tr = editor.state.tr.deleteRange(pos, pos + 1)

          tr = tr.insert(pos, parsed.content)
          editor.view.dispatch(tr)
        }
      } catch (err) {
        console.error('Image fetch error:', err)
      }
    }
  }

  return {
    isPickingImage,
    pickImage,
    pickImagePreloaded,
    insertPreloadImage,
    findImagesToUpload,
    findImagesForPlaceholders,
    addEnhancedImages,
    checkParagraphsForKeywords,
    checkPlaceholders,
    getImagesForPlaceholders,
  }
})
